import { useState, useEffect, useCallback, useRef } from "react";
import {
  Headset, X, MessageSquareText, HelpCircle, Send, Loader2, ChevronLeft, Plus,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { API } from "../App";

const WHATSAPP_URL = "https://wa.me/5598981684447?text=Ol%C3%A1%2C%20sou%20franqueado%20GoFood%20e%20preciso%20de%20suporte.";

const FAQ = [
  { q: "Como cadastro uma nova loja?", a: "Acesse Lojas → Cadastrar Loja. Preencha os dados do responsável com uma senha temporária — o lojista será obrigado a trocá-la no primeiro acesso. A loja já nasce vinculada à sua cidade." },
  { q: "Como solicito um saque?", a: "Em Financeiro → Solicitar Saque, informe valor e chave PIX. O mínimo é R$ 20,00 e há taxa fixa descontada do valor líquido. O saque passa por aprovação do administrador." },
  { q: "Quando minhas comissões são liberadas?", a: "Comissões de GMV e mensalidades entram como 'A liberar' e são liberadas automaticamente após o período de segurança. Acompanhe pelo extrato no Financeiro." },
  { q: "Como agendo um comunicado?", a: "Em Comunicados, escreva a mensagem, escolha a audiência e defina data/hora futura no campo de agendamento. Você pode cancelar antes do disparo." },
  { q: "Posso excluir uma loja?", a: "Não. O franqueado pode ativar, desativar, suspender e editar lojas. A exclusão definitiva é restrita ao Administrador Master." },
  { q: "Como altero a taxa de entrega da cidade?", a: "Em Configurações, ajuste a taxa base e por km, o horário de operação e o raio de cobertura da sua cidade." },
];

export const FranchiseeSupportButton = () => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("menu"); // menu | tickets | help
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => { if (!open) setView("menu"); }, [open]);

  return (
    <div className="fixed bottom-5 right-5 z-40" ref={wrapRef} data-testid="fr-support-widget">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute bottom-16 right-0 w-[92vw] max-w-sm bg-[#13161A] border border-white/10 rounded-2xl shadow-2xl shadow-black/60 overflow-hidden"
            data-testid="fr-support-panel"
          >
            {view === "menu" && (
              <div className="p-4 space-y-2">
                <h3 className="text-sm font-extrabold text-white mb-3 flex items-center gap-2">
                  <Headset className="w-4 h-4 text-orange-400" /> Suporte da Franquia
                </h3>
                <button
                  onClick={() => setView("tickets")}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-zinc-950 border border-white/5 hover:border-orange-500/40 text-left transition-colors"
                  data-testid="fr-support-master-chat"
                >
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-orange-500 to-rose-500 flex items-center justify-center shrink-0">
                    <MessageSquareText className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-extrabold text-white">Chat com Administrador Master</p>
                    <p className="text-[10px] text-zinc-500">Abra um chamado direto com a equipe GoFood</p>
                  </div>
                </button>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-zinc-950 border border-white/5 hover:border-emerald-500/40 text-left transition-colors"
                  data-testid="fr-support-whatsapp"
                >
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shrink-0">
                    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  </div>
                  <div>
                    <p className="text-xs font-extrabold text-white">WhatsApp de Suporte</p>
                    <p className="text-[10px] text-zinc-500">(98) 98168-4447 — atendimento direto</p>
                  </div>
                </a>
                <button
                  onClick={() => setView("help")}
                  className="w-full flex items-center gap-3 p-3 rounded-xl bg-zinc-950 border border-white/5 hover:border-sky-500/40 text-left transition-colors"
                  data-testid="fr-support-help"
                >
                  <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-sky-500 to-blue-500 flex items-center justify-center shrink-0">
                    <HelpCircle className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="text-xs font-extrabold text-white">Central de Ajuda</p>
                    <p className="text-[10px] text-zinc-500">Dúvidas frequentes do painel</p>
                  </div>
                </button>
              </div>
            )}
            {view === "tickets" && <TicketsView onBack={() => setView("menu")} />}
            {view === "help" && <HelpView onBack={() => setView("menu")} />}
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        onClick={() => setOpen((o) => !o)}
        className="w-13 h-13 p-3.5 rounded-full bg-gradient-to-br from-orange-500 to-rose-500 shadow-[0_4px_24px_rgba(255,107,0,0.45)] flex items-center justify-center"
        data-testid="fr-support-fab"
        aria-label="Suporte"
      >
        {open ? <X className="w-5 h-5 text-white" /> : <Headset className="w-5 h-5 text-white" />}
      </motion.button>
    </div>
  );
};

function TicketsView({ onBack }) {
  const [tickets, setTickets] = useState(null);
  const [active, setActive] = useState(null); // ticket object
  const [creating, setCreating] = useState(false);

  const auth = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/support/tickets?page_size=20`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      if (r.ok) {
        const d = await r.json();
        setTickets(d.items || d.tickets || []);
      }
    } catch (e) { console.warn("tickets", e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (creating) return <NewTicketView onBack={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />;
  if (active) return <TicketThreadView ticket={active} onBack={() => { setActive(null); load(); }} />;

  return (
    <div data-testid="fr-tickets-view">
      <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between">
        <button onClick={onBack} className="text-zinc-400 hover:text-white flex items-center gap-1 text-xs font-bold">
          <ChevronLeft className="w-4 h-4" /> Voltar
        </button>
        <h3 className="text-xs font-extrabold text-white">Meus Chamados</h3>
        <button
          onClick={() => setCreating(true)}
          className="px-2.5 py-1 rounded-full bg-orange-500/20 border border-orange-400/50 text-orange-300 text-[10px] font-extrabold flex items-center gap-1"
          data-testid="fr-ticket-new-btn"
        >
          <Plus className="w-3 h-3" /> Novo
        </button>
      </div>
      <div className="max-h-[50vh] overflow-y-auto">
        {tickets === null ? (
          <div className="flex items-center justify-center py-10 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Carregando…</div>
        ) : tickets.length === 0 ? (
          <p className="text-xs text-zinc-500 italic text-center py-10">Nenhum chamado aberto ainda.</p>
        ) : (
          tickets.map((t) => (
            <button
              key={t.id}
              onClick={() => setActive(t)}
              className="w-full text-left px-4 py-2.5 border-b border-white/[0.03] hover:bg-white/[0.03]"
              data-testid={`fr-ticket-item-${t.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-extrabold text-white truncate">{t.subject}</p>
                <span className={`px-1.5 py-0.5 rounded-full text-[8px] font-extrabold uppercase shrink-0 ${
                  t.status === "open" ? "bg-emerald-500/15 text-emerald-300" :
                  t.status === "in_progress" ? "bg-amber-500/15 text-amber-300" :
                  "bg-zinc-700/40 text-zinc-400"
                }`}>{{ open: "Aberto", in_progress: "Em análise", resolved: "Resolvido", closed: "Fechado" }[t.status] || t.status}</span>
              </div>
              <p className="text-[10px] text-zinc-500 mt-0.5">{new Date(t.last_message_at || t.created_at).toLocaleString("pt-BR")}{t.unread_user > 0 && <span className="text-orange-400 font-bold"> · {t.unread_user} nova(s)</span>}</p>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

function NewTicketView({ onBack, onDone }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (subject.trim().length < 3 || message.trim().length < 3) { toast.error("Preencha assunto e mensagem"); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/support/tickets`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({ subject: subject.trim(), message: message.trim(), category: "other", priority: "normal" }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Falha ao abrir chamado");
      toast.success("Chamado aberto! O Master responderá em breve.");
      onDone();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-4 space-y-2" data-testid="fr-ticket-new-view">
      <button onClick={onBack} className="text-zinc-400 hover:text-white flex items-center gap-1 text-xs font-bold mb-1">
        <ChevronLeft className="w-4 h-4" /> Voltar
      </button>
      <input
        value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={120}
        placeholder="Assunto"
        className="w-full bg-zinc-950 border border-white/10 rounded-xl px-3 py-2 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-orange-500/60"
        data-testid="fr-ticket-subject"
      />
      <textarea
        value={message} onChange={(e) => setMessage(e.target.value)} rows={4} maxLength={4000}
        placeholder="Descreva sua dúvida ou problema…"
        className="w-full bg-zinc-950 border border-white/10 rounded-xl px-3 py-2 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-orange-500/60 resize-none"
        data-testid="fr-ticket-message"
      />
      <button
        onClick={submit} disabled={saving}
        className="w-full py-2 rounded-xl bg-gradient-to-r from-orange-500 to-rose-500 text-white text-xs font-extrabold flex items-center justify-center gap-2 disabled:opacity-50"
        data-testid="fr-ticket-submit"
      >
        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Abrir Chamado
      </button>
    </div>
  );
}

function TicketThreadView({ ticket, onBack }) {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/support/tickets/${ticket.id}`, { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
      if (r.ok) setData(await r.json());
    } catch (e) { console.warn("thread", e); }
  }, [ticket.id]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const r = await fetch(`${API}/support/tickets/${ticket.id}/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: JSON.stringify({ text: text.trim() }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Falha ao enviar");
      setText("");
      load();
    } catch (e) { toast.error(e.message); }
    finally { setSending(false); }
  };

  const messages = data?.messages || [];

  return (
    <div data-testid="fr-ticket-thread-view">
      <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
        <button onClick={onBack} className="text-zinc-400 hover:text-white"><ChevronLeft className="w-4 h-4" /></button>
        <p className="text-xs font-extrabold text-white truncate flex-1">{ticket.subject}</p>
      </div>
      <div className="max-h-[40vh] overflow-y-auto p-3 space-y-2 bg-zinc-950/50">
        {!data ? (
          <div className="flex items-center justify-center py-8 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Carregando…</div>
        ) : messages.length === 0 ? (
          <p className="text-[11px] text-zinc-500 italic text-center py-6">Sem mensagens.</p>
        ) : (
          messages.map((m) => {
            const isAdmin = m.author_role === "admin";
            return (
              <div key={m.id} className={`flex ${isAdmin ? "justify-start" : "justify-end"}`}>
                <div className={`max-w-[80%] rounded-xl px-3 py-2 ${isAdmin ? "bg-zinc-800 text-zinc-200" : "bg-orange-500/20 text-white border border-orange-400/30"}`}>
                  <p className="text-[8px] font-extrabold uppercase opacity-60 mb-0.5">{isAdmin ? "Master" : "Você"}</p>
                  <p className="text-[11px] whitespace-pre-wrap">{m.text}</p>
                  <p className="text-[8px] opacity-50 mt-1 text-right">{new Date(m.created_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</p>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>
      <div className="p-2 border-t border-white/5 flex gap-2">
        <input
          value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Responder…"
          className="flex-1 bg-zinc-950 border border-white/10 rounded-xl px-3 py-2 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-orange-500/60"
          data-testid="fr-ticket-reply-input"
        />
        <button onClick={send} disabled={sending || !text.trim()}
          className="w-9 h-9 rounded-xl bg-gradient-to-r from-orange-500 to-rose-500 flex items-center justify-center disabled:opacity-40"
          data-testid="fr-ticket-reply-send">
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin text-white" /> : <Send className="w-3.5 h-3.5 text-white" />}
        </button>
      </div>
    </div>
  );
}

function HelpView({ onBack }) {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <div data-testid="fr-help-view">
      <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
        <button onClick={onBack} className="text-zinc-400 hover:text-white"><ChevronLeft className="w-4 h-4" /></button>
        <h3 className="text-xs font-extrabold text-white">Central de Ajuda</h3>
      </div>
      <div className="max-h-[50vh] overflow-y-auto p-2">
        {FAQ.map((f, i) => (
          <div key={i} className="border-b border-white/[0.04]">
            <button
              onClick={() => setOpenIdx(openIdx === i ? null : i)}
              className="w-full text-left px-3 py-2.5 text-xs font-bold text-zinc-200 hover:text-white flex items-center justify-between gap-2"
              data-testid={`fr-faq-${i}`}
            >
              {f.q}
              <span className="text-zinc-500">{openIdx === i ? "−" : "+"}</span>
            </button>
            {openIdx === i && <p className="px-3 pb-3 text-[11px] text-zinc-400 leading-relaxed">{f.a}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}
